import React, { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import noop from 'lodash/noop';
import type { FieldKey, FieldValue } from '../../common/types.tsx';
import type { IccContextType } from './types.tsx';

const defaultValue: IccContextType = {
	initialSummary: '',
	initialCardTypeId: null,
	initialFieldValues: new Map(),
	initialProjectId: null,
	onChangeSummary: noop,
	onChangeCardType: noop,
	onChangeFieldValue: noop,
	onChangeProject: noop,
	onCancel: noop,
	onCreate: noop,
};

export const IccContext = createContext<IccContextType>(defaultValue);

const useIccContext = () => useContext(IccContext);

const IccProvider: React.FC<{
	children: ReactNode;
	projectId?: number;
}> = ({ children, projectId: projectIdProp }) => {
	const [initialSummary, setInitialSummary] = useState<IccContextType['initialSummary']>(
		defaultValue.initialSummary,
	);
	const [initialCardTypeId, setInitialCardTypeId] = useState<IccContextType['initialCardTypeId']>(
		defaultValue.initialCardTypeId,
	);
	const [initialFieldValues, setInitialFieldValues] = useState<
		IccContextType['initialFieldValues']
	>(defaultValue.initialFieldValues);
	const [initialProjectId, setInitialProjectId] = useState<IccContextType['initialProjectId']>(
		projectIdProp ?? defaultValue.initialProjectId,
	);

	const onChangeSummary = useCallback((summary: string) => {
		setInitialSummary(summary);
	}, []);

	const onChangeProject = useCallback((projectId: number) => {
		setInitialProjectId(projectId);
	}, []);

	const onChangeCardType = useCallback((cardTypeId: IccContextType['initialCardTypeId']) => {
		setInitialCardTypeId(cardTypeId);
	}, []);

	const onChangeFieldValue = useCallback((fieldKey: FieldKey, fieldValue: FieldValue) => {
		setInitialFieldValues((prevFieldValues) => {
			const updatedFieldValues = new Map(prevFieldValues);
			updatedFieldValues.set(fieldKey, fieldValue);
			return updatedFieldValues;
		});
	}, []);

	const onCancel = useCallback(() => {
		setInitialSummary('');
		setInitialCardTypeId(null);
	}, []);

	const onCreate = useCallback(() => {
		setInitialSummary('');
	}, []);

	return (
		<IccContext.Provider
			value={{
				initialSummary,
				initialCardTypeId,
				initialFieldValues,
				initialProjectId,
				onChangeCardType,
				onChangeSummary,
				onChangeFieldValue,
				onChangeProject,
				onCancel,
				onCreate,
			}}
		>
			{children}
		</IccContext.Provider>
	);
};

export { IccProvider, useIccContext };
